import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Package, HelpCircle, Database, RefreshCw, CheckCircle2, Users, Mail } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    products: 0,
    questions: 0,
    leads: 0,
    new_leads: 0
  });
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    const auth = localStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return;
    }
    fetchStats();
  }, []);

  const getAuthHeader = () => {
    const auth = localStorage.getItem("adminAuth");
    return auth ? { Authorization: `Basic ${auth}` } : {};
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
      // Fallback to old method
      try {
        const [productsRes, questionsRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/questions`)
        ]);
        setStats({
          products: productsRes.data.length,
          questions: questionsRes.data.length,
          leads: 0,
          new_leads: 0
        });
      } catch (e) {
        console.error("Fallback also failed:", e);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDatabase = async () => {
    setSeeding(true);
    try {
      const response = await axios.post(
        `${API}/admin/seed`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`Database seeded: ${response.data.products_created} products, ${response.data.questions_created} questions`);
      fetchStats();
    } catch (error) {
      console.error("Error seeding database:", error);
      toast.error("Failed to seed database");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Dashboard</h1>
          <p className="text-slate-600">
            Manage your AXEON Chemical product database and questionnaire system.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-[#00BCD4]/10 flex items-center justify-center">
                <Package className="w-6 h-6 text-[#00BCD4]" />
              </div>
              <span className="text-3xl font-bold text-slate-800">
                {loading ? "-" : stats.products}
              </span>
            </div>
            <h3 className="font-semibold text-slate-700">Products</h3>
            <p className="text-sm text-slate-500">Total products in database</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <HelpCircle className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-3xl font-bold text-slate-800">
                {loading ? "-" : stats.questions}
              </span>
            </div>
            <h3 className="font-semibold text-slate-700">Questions</h3>
            <p className="text-sm text-slate-500">Questionnaire items</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-3xl font-bold text-slate-800">4</span>
            </div>
            <h3 className="font-semibold text-slate-700">Active Categories</h3>
            <p className="text-sm text-slate-500">All categories available</p>
          </div>

          <div 
            className="bg-white rounded-xl border border-slate-200 p-6 cursor-pointer hover:border-[#00BCD4] transition-colors"
            onClick={() => navigate("/admin/leads")}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-3xl font-bold text-slate-800">
                {loading ? "-" : stats.leads}
              </span>
            </div>
            <h3 className="font-semibold text-slate-700">Sales Leads</h3>
            <p className="text-sm text-slate-500">
              {stats.new_leads > 0 ? (
                <span className="text-blue-600 font-medium">{stats.new_leads} new</span>
              ) : (
                "Contact form submissions"
              )}
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Database className="w-5 h-5 text-[#00BCD4]" />
            Quick Actions
          </h2>
          
          <div className="flex flex-wrap gap-4">
            <Button
              onClick={handleSeedDatabase}
              disabled={seeding}
              className="bg-[#00BCD4] hover:bg-[#00acc1]"
              data-testid="seed-database-btn"
            >
              {seeding ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Seeding...
                </>
              ) : (
                <>
                  <Database className="w-4 h-4 mr-2" />
                  Seed All Categories
                </>
              )}
            </Button>
            
            <Button
              onClick={() => navigate("/admin/products")}
              variant="outline"
            >
              <Package className="w-4 h-4 mr-2" />
              Manage Products
            </Button>
            
            <Button
              onClick={() => navigate("/admin/questions")}
              variant="outline"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Manage Questions
            </Button>

            <Button
              onClick={() => navigate("/admin/leads")}
              variant="outline"
            >
              <Users className="w-4 h-4 mr-2" />
              View Leads
            </Button>
          </div>

          <div className="mt-6 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
            <p className="text-sm text-cyan-800">
              <strong>New:</strong> Click "Seed All Categories" to populate all 4 categories (Antiscalants, Antifoulants, Cleaners, Preservatives) with products and questions. 
              This will create 13 products and 12 questions total.
            </p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
